/** @jsxImportSource @emotion/react */

import { Box, Button, Collapse, Grid, Stack, Typography } from "@mui/material";
import type { Folder } from "certiblok-api-manager";
import { Fragment, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { TransitionGroup } from "react-transition-group";
import BottomSheet from "../../../../utils/components/bottomSheet/BottomSheet";
import LoadingIconButton from "../../../../utils/components/buttons/LoadingIconButton";
import CertiblokTextInput from "../../../../utils/components/CertiblokTextInput";
import CertiblokIcon from "../../../../utils/components/icons/CertiblokIcon";
import Loading from "../../../../utils/components/Loading";
import { ModalNavigator } from "../../../../utils/components/ModalNavigator/ModalNavigator";
import WhitePopperTooltip from "../../../../utils/components/poppers/WhitePopperTooltip";
import WhiteModal from "../../../../utils/components/WhiteModal";
import { useBetterMediaQuery } from "../../../../utils/hooks/useBetterMediaQuery";
import { useMe } from "../../../../utils/hooks/useMe";
import { useCompany } from "../../../../utils/queries/globalQueries";
import { queryClient } from "../../../../utils/reactQueryClient";
import FolderOrAuditRoomNamePicker from "../FolderNamePicker";
import AlreadyInLockedFolderBanner from "./components/AlreadyInLockedFolderBanner";
import EditCreateFolderLockedSwitch from "./components/EditCreateFolderLockedSwitch";
import { FilteredTeamsList } from "./components/FilteredTeamsList";
import { FilteredUsersList } from "./components/FilteredUsersList";
import MemberInFolder from "./components/MemberInFolder";
import TeamMembersGuestsGroupsAutocomplete from "./components/TeamMembersGuestsGroupsAutocomplete";
import { getUseEditCreateFolderFormKey } from "./hooks/useFetchFolderData";
import { useInitEditCreateFolderForm } from "./hooks/useInitEditCreateFolderForm";
import { useModalManagement } from "./hooks/useModalManagement";
import { useEditCreateEditRoomFolder, useEditCreateFolder } from "./queries/editCreateFolderModalQueries";
import { NewlyInvitedMembersList } from "./components/NewlyInvitedMembersList";

export type EditCreateFolderModalProps = {
	onSuccess?: (folder: Folder) => void;
	isInEditRoom?: boolean;
} & (
	| { isEdit: true; folder: Folder; parentFolderId?: never }
	| { isEdit?: false; parentFolderId?: string; folder?: never }
);

const EditCreateFolderModal = ModalNavigator.create<EditCreateFolderModalProps>(
	({ isEdit, onSuccess, folder, parentFolderId, isInEditRoom }) => {
		const { me } = useMe();
		const { t } = useTranslation();
		const location = useLocation();
		const isMd = useBetterMediaQuery("md");
		const { bottomSheetProps, popBottomSheet, visible } = useModalManagement();

		const isEditRoom = location?.pathname?.includes("/edit_rooms/");

		const { data: companyData, isLoading: isCompanyLoading } = useCompany();

		const { form, isLoading, parentFolder, isParentRestricted } = useInitEditCreateFolderForm({
			folder,
			parentFolderId,
			isEditRoom: isInEditRoom,
		});

		const [filterValue, setFilterValue] = useState("");

		const { control, handleSubmit, watch, setValue } = form;
		const folderName = watch("name");
		const isReserved = watch("isReserved");
		const reservedToUsers = watch("reservedToUsers");
		const reservedToTeams = watch("reservedToTeams");

		const editCreateEditRoomFolder = useEditCreateEditRoomFolder();
		const editCreateFolder = useEditCreateFolder();
		const isLoadingSubmit = editCreateFolder.isLoading || editCreateEditRoomFolder.isLoading;

		const onSubmit = handleSubmit((data) => {
			if (me) {
				let mutation;
				const reservedToUserIds =
					data.isReserved || isParentRestricted ? [...data.reservedToUsers.map((user) => user.id), me.id] : [];

				if (isEdit && isEditRoom) {
					mutation = editCreateEditRoomFolder.mutateAsync({
						isEdit: true,
						folderId: folder.id,
						folderName: data.name,
						folderColor: data.color,
						reservedToUserIds: reservedToUserIds,
						reservedToTeamIds: data.reservedToTeams.map((group) => group.id),
					});
				} else if (isEdit) {
					const toRecursivelyAddUsers =
						(data.isReserved || isParentRestricted) && data.giveAccessToSubfolders
							? [...data.reservedToUsers.filter((value) => value.isNew).map((user) => user.id)]
							: [];
					const toRecursivelyAddTeams =
						(data.isReserved || isParentRestricted) && data.giveAccessToSubfolders
							? [...data.reservedToTeams.filter((value) => value.isNew).map((group) => group.id)]
							: [];

					mutation = editCreateFolder.mutateAsync({
						isEdit: true,
						folderId: folder.id,
						folderName: data.name,
						folderColor: data.color,
						reservedToUserIds: reservedToUserIds,
						reservedToTeamIds: data.reservedToTeams.map((group) => group.id),
						recursivelyUpdateReservedToUserIds: toRecursivelyAddUsers,
						recursivelyUpdateReservedToTeamIds: toRecursivelyAddTeams,
					});
				} else if (isEditRoom) {
					mutation = editCreateEditRoomFolder.mutateAsync({
						isEdit: false,
						folderColor: data.color,
						folderName: data.name,
						parentId: parentFolderId,
						reservedToUserIds,
						reservedToTeamIds: data.reservedToTeams.map((group) => group.id),
					});
				} else {
					mutation = editCreateFolder.mutateAsync({
						isEdit: false,
						folderColor: data.color,
						folderName: data.name,
						parentId: parentFolderId,
						reservedToUserIds: reservedToUserIds,
						reservedToTeamIds: data.reservedToTeams.map((group) => group.id),
					});
				}
				mutation.then((res) => {
					onSuccess?.(res.data);
					onClose();
				});
			}
		});

		const onClose = () => {
			if (isLoadingSubmit) return;
			queryClient.resetQueries({ queryKey: getUseEditCreateFolderFormKey(folder?.id) });
			popBottomSheet();
			form.reset();
		};

		const header = (
			<Typography
				children={isEdit ? t("folders.handleFolder") : t("folders.createFolder")}
				variant="subtitle1"
				sx={{ paddingBottom: 3 }}
			/>
		);

		const children = (
			<>
				{isLoading || isCompanyLoading ? (
					<div className="flex-grow flex items-center justify-center overflow-visible min-h-[62px]">
						<Loading title={t("folders.folderLoading")} />
					</div>
				) : (
					<Fragment>
						<Controller
							name="name"
							control={control}
							render={({ field, fieldState: { error } }) => (
								<FolderOrAuditRoomNamePicker
									folderName={field.value}
									updateFolderName={field.onChange}
									selectedColor={watch("color")}
									updateColor={(value) => setValue("color", value)}
									ColorPickerProps={{
										className: "flex-wrap md:flex-nowrap",
									}}
									textInputProps={{
										label: t("folders.folderName"),
										placeholder: t("folders.folderName"),
										onBlur: field.onBlur,
										size: "medium",
										sx: {
											backgroundColor: "backgroundSecondary.default",
										},
										error: !!error,
										helperText: error?.message,
									}}
								/>
							)}
						/>
						{!isParentRestricted && (
							<Stack direction="row" alignItems={"center"} gap={4} sx={{ paddingTop: 8 }}>
								<Box>
									<Typography children={t("folders.keepFolderPrivate")} variant="subtitle2" />
									<Typography children={t("folders.limitingFolderWarning")} variant="caption" sx={{ color: "#000000" }} />
								</Box>
								<Controller
									name="isReserved"
									control={control}
									render={({ field }) => (
										<EditCreateFolderLockedSwitch
											checked={field.value}
											onChange={(e, checked) => field.onChange(checked)}
											color="secondary"
											icon="lock_bold_01"
										/>
									)}
								/>
							</Stack>
						)}
						{isParentRestricted && <AlreadyInLockedFolderBanner sx={{ marginTop: 8 }} />}
						<Collapse in={isParentRestricted || isReserved}>
							<Box>
								{isParentRestricted ? (
									<Typography
										children={t("folders.chooseWhoToGiveAccess")}
										variant="caption"
										component="p"
										sx={{ color: "#0000008A", pt: 8, pb: 1 }}
									/>
								) : (
									<Stack direction="row" alignItems={"center"} gap={2} sx={{ pt: 10, pb: 2 }}>
										<Typography
											children={t("folders.chooseWhoCanAccess")}
											variant="caption"
											component="p"
											sx={{ color: "#0000008A" }}
										/>
										<WhitePopperTooltip enabled title={t("folders.lockingFolderDescription")}>
											<CertiblokIcon size={16} color="#0000008A" name={"comunication_outline_02"} />
										</WhitePopperTooltip>
									</Stack>
								)}
								{!isParentRestricted ? (
									<TeamMembersGuestsGroupsAutocomplete
										disabledUsersIds={reservedToUsers.map((user) => user.id)}
										onSelectUser={(user) => {
											setValue("reservedToUsers", [...reservedToUsers, { ...user, isNew: isEdit }]);
										}}
										disabledGroupsIds={reservedToTeams.map((group) => group.id)}
										onSelectGroup={(group) => {
											setValue("reservedToTeams", [...reservedToTeams, { ...group, isNew: isEdit }]);
										}}
										includeGroups
									/>
								) : (
									<CertiblokTextInput
										fullWidth
										placeholder={t("teamGroups.searchMembers")}
										value={filterValue}
										onChange={(ev) => {
											const value = ev.target.value;
											setFilterValue(value);
										}}
										color="secondary"
										sx={{ backgroundColor: (theme) => theme.palette.backgroundSecondary.default }}
									/>
								)}
								<NewlyInvitedMembersList />
								{!isParentRestricted && (
									<Typography
										children={t("folders.teamMembersAlreadyInFolder")}
										variant="caption"
										component="p"
										sx={{ pt: 4, pb: 2, color: "#0000008A" }}
									/>
								)}

								<TransitionGroup
									css={{ maxHeight: "20vh", overflow: "auto", marginTop: isParentRestricted ? "16px" : "unset" }}
								>
									<MemberInFolder user={companyData?.data.owner} isAdmin />
									<FilteredTeamsList
										filterValue={filterValue}
										isParentRestricted={isParentRestricted}
										parentTeams={parentFolder?.reservedToTeams}
									/>
									<FilteredUsersList
										filterValue={filterValue}
										isParentRestricted={isParentRestricted}
										parentUsers={parentFolder?.reservedToUsers}
									/>
								</TransitionGroup>
							</Box>
						</Collapse>
					</Fragment>
				)}
			</>
		);

		const footer = (
			<Grid container justifyContent="space-between" spacing={1} sx={{ width: "100%", paddingTop: 9 }}>
				<Grid item xs="auto">
					<Button
						disabled={isLoadingSubmit}
						color="secondary"
						variant="outlined"
						size="medium"
						sx={{ gap: 2 }}
						onClick={onClose}
					>
						<CertiblokIcon size={24} color="inherit" name={"close"} />
						{t("global.cancel")}
					</Button>
				</Grid>

				<Grid item xs="auto">
					<LoadingIconButton
						size="medium"
						variant="contained"
						color="secondary"
						icon={<CertiblokIcon size={24} color="inherit" name={"success"} />}
						loading={isLoadingSubmit}
						sx={{ gap: 2 }}
						onClick={onSubmit}
						disabled={folderName === "" || isLoadingSubmit}
					>
						{isEdit ? t("global.saveEdits") : t("folders.createFolder")}
					</LoadingIconButton>
				</Grid>
			</Grid>
		);

		return (
			<FormProvider {...form}>
				{isMd ? (
					<WhiteModal
						open={visible}
						onClose={onClose}
						keepMounted={false}
						containerSx={{
							borderRadius: 6,
							maxWidth: 720,
							width: "90vw",
						}}
						responsive
					>
						<Stack sx={{ height: "100%", minHeight: 280 }}>
							{header}
							{children}
							{footer}
						</Stack>
					</WhiteModal>
				) : (
					<BottomSheet {...bottomSheetProps} open={visible} onClose={onClose} header={header} footer={footer}>
						<div className="px-5 min-h-[420px]">{children}</div>
					</BottomSheet>
				)}
			</FormProvider>
		);
	}
);
export default EditCreateFolderModal;
